import React, { useEffect, useRef, useState } from 'react';
import {
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,

} from '@mui/material';

import { AllergyFormProps } from './Allergies';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import { useAuth } from '../../../context/AuthContext';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { apiHandler } from '../../../services/authService';




interface FamilyMember {
    relationship: string;
    status: boolean;
}

interface Disease {
    id: number;
    slug: string;
    name: string;
    attributes: {
        values: FamilyMember[];
    };
}

const FamilyHistory: React.FC<AllergyFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [isBottomShadowVisible, setIsBottomShadowVisible] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [diseases, setDiseases] = useState<Disease[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDiseaseId, setSelectedDiseaseId] = useState<number | null>(null);
    const [selectedFamilyIndex, setSelectedFamilyIndex] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState<boolean>(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
    const [loading, setLoading] = useState<boolean>(false);

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (tableContainerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
                setIsBottomShadowVisible(scrollTop + clientHeight < scrollHeight);
            }
        };

        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    useEffect(() => {
        const fetchDiseases = async () => {
            try {
                setLoading(true);

                const url        = `${BASE_URL}/resource/masters/all?slug=family_history_diseases&order_by=id&dir=1&patient_id=${patientId}`;
                const response   = await apiHandler('', 'GET',{}, url);
            
                if (!response) {
                    throw new Error("No response received from the server");
                }

                // const response = await fetch(`${BASE_URL}/resource/masters/all?slug=family_history_diseases&order_by=id&dir=1&patient_id=${patientId}`, {
                //     method: 'GET',
                //     headers: getAxiosConfig().headers,
                // });
                // const data = await response.json();
                const data = response.data;
                console.log('API Response:', data); // Log the API response
                if (data.code === 200) {
                    const updatedDiseases: Disease[] = data.data.masters.map((disease: any) => ({
                        id: disease.id,
                        name: disease.name,
                        slug: disease.slug,
                        attributes: {
                            values: disease.attributes.values.map((value: any) => ({
                                relationship: value.relationship,
                                status: value.status || false,
                            })),
                        },
                    }));
                    setDiseases(updatedDiseases);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                showSnackbar('Failed to fetch family history data', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchDiseases();
    }, [token, patientId]);

    const handleCheckboxClick = (diseaseId: number, familyIndex: number) => {
        const selectedDisease = diseases.find(disease => disease.id === diseaseId);
        const familyMemberStatus = selectedDisease?.attributes.values[familyIndex].status;
        setSelectedDiseaseId(diseaseId);
        setSelectedFamilyIndex(familyIndex);
        setIsAdding(!familyMemberStatus);
        setDialogOpen(true);
    };


    return (
        <div className="font-jost" style={{ fontFamily: 'Jost, sans-serif' }}>
          {loading ? (
            <LoadingAnimation />
          ) : (
            <TableContainer
              component={Paper}
              className={`max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 ${isBottomShadowVisible ? 'scroll-indicator' : ''} font-jost`}
              sx={{ fontFamily: 'Jost, sans-serif' }} // Add this line for explicit styling
              ref={tableContainerRef}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#4A90E2' }}>
                    <TableCell
                      style={{
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        backgroundColor: '#000000',
                        padding: '16px'
                      }}
                    >
                      Disease
                    </TableCell>
                    {diseases.length > 0 &&
                      Array.isArray(diseases[0].attributes.values) &&
                      diseases[0].attributes.values.map((member, index) => (
                        <TableCell
                          key={index}
                          style={{
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            backgroundColor: '#000000',
                            padding: '8px'
                          }}
                        >
                          {member.relationship}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
    
                <TableBody>
                  {diseases.map((disease) => (
                    <TableRow key={disease.id} style={{ height: '32px' }}>
                      <TableCell style={{ padding: '12px' }}>{disease.name}</TableCell>
                      {disease.attributes.values.map((familyMember, index) => (
                        <TableCell key={index} style={{ padding: '8px' }}>
                          <Checkbox
                            checked={familyMember.status}
                            onChange={() => handleCheckboxClick(disease.id, index)}
                            size="small"
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

            </TableContainer>
          )}
        </div>
      )
};

export default FamilyHistory;
